import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../store/store';
import { resolveDefaultTemplateId } from '../../../utils/sciMailWindows';
import BoitageLots from './BoitageLots.module';
import SCIsTransactionTable from './SCIsTransactionTable';
import { transactionMailTemplates } from './transactionMailTemplates';

const SciMailTemplates = React.lazy(() => import('../components/SciMailTemplates'));
const BoitageDocumentTemplates = React.lazy(() => import('./BoitageDocumentTemplates'));

type SectionId = 'boitage-lots' | 'boitage-document' | 'sci-selection' | 'sci-mails';

type Step = {
  id: SectionId;
  title: string;
  emoji: string;
};

function SCIsTransaction() {
  const [activeSection, setActiveSection] = useState<SectionId>('sci-selection');
  const [selectedSirens, setSelectedSirens] = useState<Set<string>>(new Set());
  const company = useSelector((state: RootState) => state.auth.company);
  const companyDefaultId = useMemo(
    () => resolveDefaultTemplateId(company?.brandingSettings?.sciMailWindows, 'transaction'),
    [company?.brandingSettings?.sciMailWindows],
  );

  const handleToggleSelection = useCallback((siren: string, name: string, checked: boolean) => {
    setSelectedSirens((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(siren);
      } else {
        next.delete(siren);
      }
      return next;
    });
  }, []);

  const handleBulkSelectionChange = useCallback((items: Array<{ siren: string; name: string }>) => {
    setSelectedSirens(() => {
      const next = new Set<string>();
      items.forEach((item) => {
        if (item.siren) {
          next.add(item.siren);
        }
      });
      return next;
    });
  }, []);

  const steps = useMemo<Step[]>(
    () => [
      { id: 'boitage-lots', title: 'Identifier le nombre de lots dans la copropriété pour le boîtage', emoji: `<span role="img" aria-label="house">🏠</span>` },
      { id: 'boitage-document', title: 'Editer le document pour la boîtage', emoji: `<span role="img" aria-label="document">📄</span>` },
      { id: 'sci-selection', title: 'Identifier les SCIS présentes dans la copropriété', emoji: `<span role="img" aria-label="building">🏢</span>` },
      { id: 'sci-mails', title: 'Choisissez le modèle de mail à envoyer aux SCI', emoji: `<span role="img" aria-label="mail">📧</span>` },
    ],
    [],
  );

  const showContent = (contentId: SectionId) => {
    setActiveSection(contentId);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'boitage-lots':
        return (
          <BoitageLots onOpenBoitageDocument={() => showContent('boitage-document')} />
        );
      case 'boitage-document':
        return (
          <Suspense fallback={<div className="p-6 text-center text-gray-500">Chargement…</div>}>
            <BoitageDocumentTemplates />
          </Suspense>
        );
      case 'sci-selection':
        return (
          <SCIsTransactionTable
            selectedSirens={selectedSirens}
            onToggleSelection={handleToggleSelection}
            onBulkSelectionChange={handleBulkSelectionChange}
          />
        );
      case 'sci-mails':
        return (
          <Suspense fallback={<div className="p-6 text-center text-gray-500">Chargement…</div>}>
            <SciMailTemplates
              mailTemplates={transactionMailTemplates}
              showQualificationWarningForTemplate={1}
              companyDefaultId={companyDefaultId}
            />
          </Suspense>
        );
      default:
        return (
          <SCIsTransactionTable
            selectedSirens={selectedSirens}
            onToggleSelection={handleToggleSelection}
            onBulkSelectionChange={handleBulkSelectionChange}
          />
        );
    }
  };

  return (
    <div className='rounded-lg bg-white p-4 shadow-md'>
      <div className="min-h-screen leading-relaxed text-slate-800">
        <div className="relative mx-5 mt-5 mb-0 overflow-hidden rounded-2xl bg-rgpd-blue px-[30px] py-5 text-center text-white">
          <div className="relative z-10">
            <h1 className="mb-2 text-xl font-bold tracking-tight">
              COMMUNICATION BIEN À VENDRE
            </h1>
            <div className="mx-auto max-w-2xl text-base font-normal opacity-90">
              Les 2 actions possibles pour informer les copropriétaires sur un bien à vendre dans leur immeuble
            </div>
          </div>
        </div>

        <div className="sticky top-5 z-20 mx-5">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* First group: INFORMATION AUX COPROPRIETES VIA LE BOITAGE */}
            <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100">
              <div className="border-b border-gray-200/80 bg-slate-50 px-6 py-3">
                <h2 className="text-center text-lg font-semibold text-slate-800">
                  ACTION 1 : INFORMATION AUX COPROPRIETES VIA LE BOITAGE
                </h2>
              </div>
              <div className="flex">
                {steps.slice(0, 2).map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => showContent(step.id)}
                    className={`flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 px-6 py-6 transition-all duration-300 hover:-translate-y-0.5 ${
                      activeSection === step.id
                        ? 'border-b-8 border-rgpd-green bg-white font-bold text-slate-800'
                        : `border-b-4 border-b-transparent bg-transparent font-semibold text-slate-500 ${index < 1 ? 'border-r-2 border-gray-300' : ''}`
                    }`}
                  >
                    <span className="text-xl" dangerouslySetInnerHTML={{ __html: step.emoji }} />
                    <span className="text-center leading-relaxed">{step.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Second group: INFORMATION AUX SCI PRÉSENTES DANS L'IMMEUBLE PAR MAIL */}
            <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100">
              <div className="border-b border-gray-200/80 bg-slate-50 px-6 py-3">
                <h2 className="text-center text-lg font-semibold text-slate-800">
                  ACTION 2 : INFORMATION AUX SCI PRÉSENTES DANS L'IMMEUBLE PAR MAIL
                </h2>
              </div>
              <div className="flex">
                {steps.slice(2, 4).map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => showContent(step.id)}
                    className={`flex flex-1 cursor-pointer flex-col items-center justify-center gap-2 px-6 py-6 transition-all duration-300 hover:-translate-y-0.5 ${
                      activeSection === step.id
                        ? 'border-b-8 border-rgpd-green bg-white font-bold text-slate-800'
                        : `border-b-4 border-b-transparent bg-transparent font-semibold text-slate-500 ${index < 1 ? 'border-r-2 border-gray-300' : ''}`
                    }`}
                  >
                    <span className="text-xl" dangerouslySetInnerHTML={{ __html: step.emoji }} />
                    <span className="text-center leading-relaxed">{step.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-8 pb-15">
          <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white">
            <div className="p-8">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SCIsTransaction;


